import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import AddNoteModal from 'components/AddNoteModal';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import CollapsibleText from 'components/CollapsibleText';
import Toaster from 'components/Toaster';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';
import { useAddAccountNote } from './gql/useAddAccountNote';
import { useDeleteAccountNote } from './gql/useDeleteAccountNote';
import { useUpdateAccountNote } from './gql/useUpdateAccountNote';

// Note type configuration with colors
const NOTE_TYPES = {
  event: { label: 'Event', color: '#8b5cf6' },
  issues: { label: 'Issues', color: '#ef4444' },
  business: { label: 'Business', color: '#3b82f6' },
  staff: { label: 'Staff', color: '#10b981' },
  hardware: { label: 'Hardware', color: '#f59e0b' },
  instructions: { label: 'Instructions', color: '#06b6d4' },
  payouts: { label: 'Payouts', color: '#84cc16' },
  other: { label: 'Other', color: '#6b7280' },
};

// Section header with title and action button
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 4px 0 0 0;
`;

const HeaderLeft = styled.div``;

const ItemCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.muted};
`;

// Wrapper for add button
const AddButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 20px;
  }
`;

// Filter section
const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
`;

const FilterChip = styled.button`
  padding: 6px 14px;
  border-radius: 20px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid
    ${({ active, theme }) =>
      active
        ? 'transparent'
        : theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : '#e2e8f0'};
  background: ${({ active, color, theme }) =>
    active
      ? color || '#fa4616'
      : theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : '#ffffff'};
  color: ${({ active, theme }) =>
    active ? '#ffffff' : theme?.name === 'dark' ? '#94a3b8' : '#64748b'};

  &:hover {
    border-color: ${({ color }) => color || '#fa4616'};
    color: ${({ active, color }) => (active ? '#ffffff' : color || '#fa4616')};
  }
`;

// Timeline container
const TimelineContainer = styled.div`
  position: relative;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 19px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};

  @media (max-width: 768px) {
    display: none;
  }
`;

// Timeline item
const TimelineItem = styled.div`
  position: relative;
  padding-left: 52px;
  padding-bottom: 24px;

  &:last-child {
    padding-bottom: 0;
  }

  @media (max-width: 768px) {
    padding-left: 0;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: 12px;
  top: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ color }) => color || '#fa4616'};
  border: 3px solid
    ${({ theme }) => (theme?.name === 'dark' ? '#1F2937' : '#ffffff')};
  box-shadow: 0 0 0 2px
    ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};

  @media (max-width: 768px) {
    display: none;
  }
`;

const NoteCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0')};
  border-radius: 12px;
  padding: 16px 20px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1'};
    box-shadow: ${({ theme }) =>
      theme?.name === 'dark'
        ? '0 4px 12px rgba(0, 0, 0, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.08)'};
  }
`;

const NoteHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const NoteTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  flex: 1;
`;

const NoteAttachment = styled.div`
  margin: 12px 0;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
  }

  img {
    max-width: 200px;
    max-height: 150px;
    border-radius: 8px;
    transition: all 0.2s;
    border: 1px solid
      ${({ theme }) =>
        theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};

    &:hover {
      transform: scale(1.02);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
`;

const PdfAttachment = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(239, 68, 68, 0.1)'
      : 'rgba(239, 68, 68, 0.08)'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(239, 68, 68, 0.3)'
        : 'rgba(239, 68, 68, 0.2)'};
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(239, 68, 68, 0.15)'
        : 'rgba(239, 68, 68, 0.12)'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
  }

  svg {
    flex-shrink: 0;
  }
`;

const PdfLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#FCA5A5' : '#DC2626')};
`;

const PdfIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
      stroke="#EF4444"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 2V8H20"
      stroke="#EF4444"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <text x="7" y="17" fontSize="6" fontWeight="bold" fill="#EF4444">
      PDF
    </text>
  </svg>
);

// Helper to check if URL is a PDF
const isPdfUrl = (url) => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  return (
    lowerUrl.endsWith('.pdf') ||
    lowerUrl.includes('.pdf?') ||
    lowerUrl.includes('/pdf')
  );
};

const NoteFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const NoteMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const NoteDate = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NoteAuthor = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NoteActions = styled.div`
  display: flex;
  gap: 8px;
`;

// Wrapper for action buttons
const ActionButtonWrapper = styled.div`
  button {
    width: 32px;
    height: 32px;
    padding: 0;
  }
`;

// Empty state
const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${({ theme }) => theme.colors.primary};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0 0 24px 0;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
`;

// Icons
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const NotesIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

// Helper to format date
const formatDate = (dateValue) => {
  if (!dateValue) return 'Just now';

  // Handle Unix timestamp (as string or number)
  let date;
  if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
    // It's a numeric string (Unix timestamp in milliseconds)
    date = new Date(parseInt(dateValue, 10));
  } else if (typeof dateValue === 'number') {
    date = new Date(dateValue);
  } else {
    date = new Date(dateValue);
  }

  // Check if date is valid
  if (isNaN(date.getTime())) return 'Just now';

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

const NotesTab = ({ notes = [], setFieldValue, accountId }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [toaster, setToaster] = useState({ isOpen: false });
  const { addAccountNote } = useAddAccountNote();
  const { deleteAccountNote } = useDeleteAccountNote();
  const { updateAccountNote } = useUpdateAccountNote();

  // Sort notes by date (newest first) and filter
  const filteredNotes = useMemo(() => {
    const sorted = [...notes].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    if (activeFilter === 'all') return sorted;
    return sorted.filter((note) => note.type === activeFilter);
  }, [notes, activeFilter]);

  const handleAddNote = async (noteData, isEditing) => {
    if (isEditing) {
      const result = await updateAccountNote({
        id: parseInt(noteData.id),
        title: noteData.title,
        note: noteData.note,
        type: noteData.type,
        ...(noteData.image_id !== undefined && {
          image_id: noteData.image_id ? parseInt(noteData.image_id) : null,
        }),
      });

      if (result.success) {
        // Update local state with the returned data
        const updatedNotes = notes.map((n) =>
          n.id === noteData.id ? { ...n, ...result.data } : n
        );
        setFieldValue('notes', updatedNotes);
        setToaster({
          isOpen: true,
          type: 'success',
          title: 'Success!',
          message: 'Note updated successfully.',
        });
        return true;
      } else {
        setToaster({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: result.error || 'Failed to update note.',
        });
        return false;
      }
    }

    const result = await addAccountNote({
      account_id: parseInt(accountId),
      title: noteData.title,
      note: noteData.note,
      type: noteData.type,
      ...(noteData.image_id && { image_id: parseInt(noteData.image_id) }),
    });

    if (result.success) {
      // Add the new note to local state
      setFieldValue('notes', [result.data, ...notes]);
      setToaster({
        isOpen: true,
        type: 'success',
        title: 'Success!',
        message: 'Note added successfully.',
      });
      return true;
    } else {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: result.error || 'Failed to add note.',
      });
      return false;
    }
  };

  const handleEditClick = (note) => {
    setSelectedNote(note);
    setShowAddModal(true);
  };

  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;

    const result = await deleteAccountNote(parseInt(noteToDelete.id));

    if (result.success) {
      const updatedNotes = notes.filter((note) => note.id !== noteToDelete.id);
      setFieldValue('notes', updatedNotes);
      setToaster({
        isOpen: true,
        type: 'success',
        title: 'Success!',
        message: 'Note deleted successfully.',
      });
    } else {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: result.error || 'Failed to delete note.',
      });
    }

    setShowDeleteModal(false);
    setNoteToDelete(null);
  };

  // Get counts for each type
  const typeCounts = useMemo(() => {
    const counts = { all: notes.length };
    notes.forEach((note) => {
      counts[note.type] = (counts[note.type] || 0) + 1;
    });
    return counts;
  }, [notes]);

  return (
    <>
      <SectionHeader>
        <HeaderLeft>
          <SectionTitle>
            <NotesIcon />
            Account Notes
            {notes.length > 0 && <ItemCount>({notes.length})</ItemCount>}
          </SectionTitle>
          <SectionSubtitle>
            Add and manage internal notes about this account. Notes are only
            visible to administrators.
          </SectionSubtitle>
        </HeaderLeft>
        <AddButtonWrapper>
          <ButtonV2
            elevated
            type="button"
            onClick={() => setShowAddModal(true)}
            icon={<PlusIcon />}
          >
            Add Note
          </ButtonV2>
        </AddButtonWrapper>
      </SectionHeader>

      {notes.length > 0 && (
        <FilterSection>
          <FilterChip
            active={activeFilter === 'all'}
            onClick={() => setActiveFilter('all')}
          >
            All ({typeCounts.all || 0})
          </FilterChip>
          {Object.entries(NOTE_TYPES).map(([key, { label, color }]) =>
            typeCounts[key] ? (
              <FilterChip
                key={key}
                active={activeFilter === key}
                color={color}
                onClick={() => setActiveFilter(key)}
              >
                {label} ({typeCounts[key]})
              </FilterChip>
            ) : null
          )}
        </FilterSection>
      )}

      {filteredNotes.length === 0 ? (
        <Card>
          <EmptyState>
            <EmptyStateIcon>
              <NotesIcon />
            </EmptyStateIcon>
            <EmptyStateTitle>
              {activeFilter === 'all' ? 'No Notes Yet' : 'No Notes Found'}
            </EmptyStateTitle>
            <EmptyStateText>
              {activeFilter === 'all'
                ? 'Add notes to keep track of important information about this account.'
                : `No notes found for the "${NOTE_TYPES[activeFilter]?.label}" category.`}
            </EmptyStateText>
            {activeFilter === 'all' && (
              <AddButtonWrapper>
                <ButtonV2
                  elevated
                  type="button"
                  onClick={() => setShowAddModal(true)}
                  icon={<PlusIcon />}
                >
                  Add Your First Note
                </ButtonV2>
              </AddButtonWrapper>
            )}
          </EmptyState>
        </Card>
      ) : (
        <TimelineContainer>
          <TimelineLine />
          {filteredNotes.map((note) => {
            const typeConfig = NOTE_TYPES[note.type] || NOTE_TYPES.other;
            return (
              <TimelineItem key={note.id}>
                <TimelineDot />
                <NoteCard>
                  <NoteHeader>
                    <NoteTitle>{note.title}</NoteTitle>
                    <Badge uppercase size="small">
                      {typeConfig.label}
                    </Badge>
                  </NoteHeader>
                  <CollapsibleText text={note.note} maxLength={300} />
                  {note.image && (
                    <NoteAttachment>
                      {isPdfUrl(note.image) ? (
                        <PdfAttachment
                          href={note.image}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <PdfIcon />
                          <PdfLabel>View PDF Attachment</PdfLabel>
                        </PdfAttachment>
                      ) : (
                        <a
                          href={note.image}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img src={note.image} alt="Note attachment" />
                        </a>
                      )}
                    </NoteAttachment>
                  )}
                  <NoteFooter>
                    <NoteMeta>
                      {note.created_by?.formatted_name && (
                        <NoteAuthor>
                          <UserIcon />
                          {note.created_by.formatted_name}
                        </NoteAuthor>
                      )}
                      <NoteDate>
                        <ClockIcon />
                        {formatDate(note.created_at)}
                      </NoteDate>
                    </NoteMeta>
                    <NoteActions>
                      <ActionButtonWrapper>
                        <ButtonV2
                          variant="icon"
                          type="button"
                          onClick={() => handleEditClick(note)}
                          title="Edit note"
                          icon={<EditIcon />}
                        />
                      </ActionButtonWrapper>
                      <ActionButtonWrapper>
                        <ButtonV2
                          danger
                          variant="icon"
                          type="button"
                          onClick={() => handleDeleteClick(note)}
                          title="Delete note"
                          icon={<TrashIcon />}
                        />
                      </ActionButtonWrapper>
                    </NoteActions>
                  </NoteFooter>
                </NoteCard>
              </TimelineItem>
            );
          })}
        </TimelineContainer>
      )}

      <AddNoteModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedNote(null);
        }}
        onSubmit={handleAddNote}
        note={selectedNote}
        defaultType="other"
        modalTitle="Add Note"
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setNoteToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={noteToDelete?.title || ''}
        itemType="note"
      />

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </>
  );
};

NotesTab.propTypes = {
  notes: PropTypes.array,
  setFieldValue: PropTypes.func.isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default NotesTab;
